import { DonutChart } from "@mantine/charts";
import { Box, Flex, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { ceil, round, subtract } from "mathjs";

import { builder } from "@/builders";
import { useFakeAdminDashboardData } from "@/builders/types/admin-dashboard";
import { useCurrentORG } from "@/components/super-admin/org-management/use-organisation";
import { partial } from "@/libraries/partial";
import clsx from "clsx";

export function PerfomanceMetrics() {
  const initialDashboardData = useFakeAdminDashboardData();
  const { organization } = useCurrentORG();

  const { data, isPlaceholderData } = useQuery({
    queryKey: builder.data.admin_dashboard.$get({
      organization: organization.id,
    }),
    queryFn: () =>
      builder.$use.data.admin_dashboard({
        organization: organization.id,
      }),
    select: ({ data }) => data,
    placeholderData: {
      data: initialDashboardData,
    },
  });

  const { average_course_completion_rate = 0 } = partial(data);
  const completion = ceil(average_course_completion_rate ?? 0);
  const incomplete = round(subtract(100, completion), 2);

  return (
    <Stack
      p={20}
      gap={18}
      flex={1}
      className="basis-[3/7] rounded-lg border border-primary-border-light bg-primary-background-white"
    >
      <Title
        className={clsx("prose-xl/medium text-primary-text-body", {
          skeleton: isPlaceholderData,
        })}
      >
        Engagement & Performance Metrics
      </Title>
      <Stack
        className={clsx("rounded bg-accent-3", {
          skeleton: isPlaceholderData,
        })}
        p={20}
        flex={1}
        justify="space-between"
      >
        <Title order={3} className="prose-base/medium text-primary-text-body">
          Average Course Completion Rate
        </Title>

        <DonutChart
          styles={{
            label: {
              fontSize: "64px",
              fontWeight: 500,
            },
          }}
          thickness={9}
          size={250}
          chartLabel={`${completion}%`}
          className="self-center"
          withTooltip={false}
          startAngle={225}
          endAngle={-40}
          paddingAngle={2}
          data={[
            {
              name: "Average Course Completion Rate",
              value: completion,
              color: "var(--accent-9)",
            },
            {
              name: "Average Course Uncompleted",
              value: incomplete,
              color: "var(--accent-6)",
            },
          ]}
        />

        <Flex gap={16} justify="center">
          <Flex gap={4} align="center">
            <Box
              display="inline-block"
              className="rounded-full bg-accent-9 size-3"
            />
            <Text span className="prose-sm/regular text-primary-text-subtle">
              Completed
            </Text>
            <Text span className="prose-sm/regular text-primary-text-body">
              {completion}%
            </Text>
          </Flex>

          <Flex gap={4} align="center">
            <Box
              display="inline-block"
              className="rounded-full bg-accent-6 size-3"
            />
            <Text span className="prose-sm/regular text-primary-text-subtle">
              Not completed
            </Text>
            <Text span className="prose-sm/regular text-primary-text-body">
              {incomplete}%
            </Text>
          </Flex>
        </Flex>
      </Stack>
    </Stack>
  );
}
